import React from 'react';
import { Link } from 'react-router-dom';
import { Report } from '../hooks/useReports';

interface TreasureMapPopupProps {
  report: Report;
  onSelect: () => void;
}

const TreasureMapPopup: React.FC<TreasureMapPopupProps> = ({ report, onSelect }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-verge text-white';
      case 'going_soon':
        return 'bg-treasure text-urban';
      case 'gone':
        return 'bg-rust text-white';
      default:
        return 'bg-urban text-white';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'going_soon':
        return 'Going Soon';
      case 'gone':
        return 'Gone';
      default:
        return status;
    }
  };

  const formatDate = (date: any) => {
    try {
      // Handle Firestore Timestamp objects
      const dateObj = date?.toDate ? date.toDate() : new Date(date);
      
      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        return 'Invalid date';
      }
      
      return new Intl.DateTimeFormat('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(dateObj);
    } catch (error) {
      console.warn('Error formatting date:', error, date);
      return 'Invalid date';
    }
  };

  return (
    <div className="w-72 max-w-sm">
      {/* Image */}
      {report.images && report.images.length > 0 && (
        <div className="relative mb-3 -mx-3 -mt-3">
          <img
            src={report.images[0].url}
            alt={report.images[0].altText || 'Curbside treasure item'}
            className="w-full h-32 object-cover rounded-t-lg"
          />
          {/* Status badge overlay */}
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
              {getStatusText(report.status)}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="space-y-2">
        {/* Title removed - focusing on visual content and location */}

        {/* Location */}
        <div className="flex items-center gap-1 text-xs text-urban/70">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>{report.location.suburb}, {report.location.postcode}</span>
        </div>

        {/* Tags */}
        {report.tags && report.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {report.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-verge/10 text-verge text-xs rounded-full"
              >
                {String(tag)}
              </span>
            ))}
            {report.tags.length > 3 && (
              <span className="px-2 py-0.5 bg-urban/10 text-urban text-xs rounded-full">
                +{report.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="flex items-center justify-between text-xs text-urban/70">
          <span>
            {formatDate(report.submittedAt)}
          </span>
          {report.viewCount > 0 && (
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {report.viewCount}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-urban/10">
          <button
            onClick={onSelect}
            className="flex-1 bg-cardboard/10 text-cardboard hover:bg-cardboard hover:text-white text-xs font-medium py-2 px-3 rounded-md transition-all duration-200 hover:scale-105 hover:shadow-md"
          >
            Select
          </button>
          <Link
            to={`/reports/${report.id}`}
            className="flex-1 bg-cardboard text-white hover:bg-cardboard-600 text-xs font-medium py-2 px-3 rounded-md transition-all duration-200 hover:scale-105 hover:shadow-md text-center"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TreasureMapPopup;